import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Activity, FileText, Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { Lead } from '../../../../common/hooks/use-leads-api.hook';
import { MiniStat, Thumb } from '../../../../components/design-system/analytics';
import { RelativeTime, StatusBadge, formatDate, formatNumber } from '../../../../components/design-system/admin';
import { LeadReading, leadSource } from '../../../../modules/vendor/analytics/viz/lead-temperature';
import { VIZ_LINKS } from '../../../../modules/vendor/analytics/viz/links';
import { TemperatureBadge } from './TemperatureBadge';

/**
 * One lead: how warm and why, what they looked at, where they came from, and
 * the next thing to do — write to them, or read their visits first.
 */
export function LeadSheet({ lead, reading, onClose }: { lead: Lead | null; reading?: LeadReading; onClose: () => void }) {
  const { t } = useTranslation();
  const source = lead ? leadSource(lead) : null;
  const a = lead?.attribution;

  return (
    <Sheet open={!!lead} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-lg" data-testid="lead-sheet">
        {lead && reading && (
          <>
            <SheetHeader className="flex-row items-center gap-3 border-b p-5 pe-12">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full border bg-muted text-sm font-medium" aria-hidden>
                {lead.email.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <SheetTitle className="truncate">{lead.email}</SheetTitle>
                <SheetDescription className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <TemperatureBadge temperature={reading.temperature} />
                  <span>·</span>
                  <span>{t('VIZ_ANALYTICS.LEADS.SCORE', { score: reading.score })}</span>
                  {source && (
                    <>
                      <span>·</span>
                      <span className="truncate">{source}</span>
                    </>
                  )}
                </SheetDescription>
              </div>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              {/* The next step first: that is why the sheet was opened. */}
              <div className="rounded-xl border p-4">
                <p className="text-sm font-medium">{t(`VIZ_ANALYTICS.LEADS.NEXT.${reading.action.toUpperCase()}`)}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {lead.unsubscribedAt
                    ? t('VIZ_ANALYTICS.LEADS.CONSENT_UNSUBSCRIBED', { date: formatDate(lead.unsubscribedAt) })
                    : lead.marketingConsent
                      ? t('VIZ_ANALYTICS.LEADS.CONSENT_YES')
                      : t('VIZ_ANALYTICS.LEADS.CONSENT_NO')}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {/* The whole story — room, render, requests, chat, follow-up — on the lead desk. */}
                  <Button asChild size="sm" data-testid="lead-sheet-open-file">
                    <Link to={`/sales/leads/${lead._id}`}>
                      <FileText />
                      {t('LEAD_DESK.OPEN_FILE')}
                    </Link>
                  </Button>
                  {!lead.unsubscribedAt && (
                    <Button asChild variant="outline" size="sm">
                      <Link to={VIZ_LINKS.compose(lead.email)}>
                        <Mail />
                        {t('VIZ_ANALYTICS.LEADS.WRITE')}
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="ghost" size="sm">
                    <Link to={VIZ_LINKS.activityFor(lead.email)}>
                      <Activity />
                      {t('VIZ_ANALYTICS.LEADS.SEE_ACTIVITY')}
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <MiniStat label={t('VIZ_ANALYTICS.LEADS.COL.DOWNLOADS')} value={formatNumber(lead.downloadCount ?? 0)} />
                <MiniStat label={t('VIZ_ANALYTICS.LEADS.EVENTS')} value={formatNumber(lead.eventCount ?? 0)} />
                <MiniStat label={t('VIZ_ANALYTICS.LEADS.COL.PRODUCTS')} value={formatNumber(lead.products?.length ?? 0)} />
                <MiniStat label={t('VIZ_ANALYTICS.LEADS.EMAILS_SENT')} value={formatNumber(lead.emailsSent ?? 0)} />
              </div>

              {reading.reasons.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">{t('VIZ_ANALYTICS.LEADS.WHY')}</p>
                  <ul className="flex flex-col gap-1.5">
                    {reading.reasons.map((reason) => (
                      <li key={reason}>
                        <StatusBadge tone={reason === 'GONE_QUIET' ? 'neutral' : 'info'}>{t(`VIZ_ANALYTICS.LEADS.REASON.${reason}`)}</StatusBadge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.FIRST_SEEN')}</dt>
                  <dd className="mt-0.5">{formatDate(lead.firstSeenAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.COL.LAST_SEEN')}</dt>
                  <dd className="mt-0.5">
                    <RelativeTime value={lead.lastSeenAt} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.LAST_DOWNLOAD')}</dt>
                  <dd className="mt-0.5">{lead.lastDownloadAt ? <RelativeTime value={lead.lastDownloadAt} /> : '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.LAST_EMAILED')}</dt>
                  <dd className="mt-0.5">{lead.lastEmailedAt ? <RelativeTime value={lead.lastEmailedAt} /> : t('VIZ_ANALYTICS.LEADS.NEVER_EMAILED')}</dd>
                </div>
              </dl>

              <div>
                <p className="mb-3 text-sm font-medium">{t('VIZ_ANALYTICS.LEADS.PRODUCTS_TITLE', { count: lead.products?.length ?? 0 })}</p>
                {lead.products?.length ? (
                  <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[...lead.products]
                      .sort((x, y) => (y.usageCount ?? 0) - (x.usageCount ?? 0))
                      .map((p) => (
                        <li key={p.productId} className="min-w-0 overflow-hidden rounded-lg border">
                          <Thumb src={p.thumbnailPath} alt={p.title} size="fill" className="aspect-square w-full rounded-none border-0" />
                          <div className="p-2">
                            <p className="truncate text-xs">{p.title}</p>
                            <p className="text-[11px] tabular-nums text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.TRIED_TIMES', { count: p.usageCount ?? 1 })}</p>
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.LEADS.NO_PRODUCTS')}</p>
                )}
              </div>

              {a && (a.source || a.medium || a.campaign || a.referrer) && (
                <div>
                  <p className="mb-2 text-sm font-medium">{t('VIZ_ANALYTICS.LEADS.SOURCE_TITLE')}</p>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {(['source', 'medium', 'campaign', 'referrer'] as const).map((k) =>
                      a[k] ? (
                        <div key={k} className="min-w-0">
                          <dt className="text-xs text-muted-foreground">{t(`VIZ_ANALYTICS.LEADS.ATTRIBUTION.${k.toUpperCase()}`)}</dt>
                          <dd className="mt-0.5 truncate">{String(a[k])}</dd>
                        </div>
                      ) : null,
                    )}
                  </dl>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
